/**
 * Mouse gestures read from the keybinding table (Synth VR). The scene used to test the modifier keys
 * itself — "Alt held", "Ctrl held" — so a gesture shown in Settings → Keys could be rebound there with no
 * effect. Each gesture now asks the table whether this press is the one bound to it, so the row and the
 * behaviour are one thing.
 *
 * The table names mouse buttons as the desktop editor does: `Mouse0` is the left button, `Mouse1` the
 * right and `Mouse2` the middle, the reverse of the DOM's 1 and 2 (`domButtonToKey`).
 */
import { useEditorStore } from '../../store/editorStore';
import { isSynthVrView } from '../../store/editorSettings';
import { comboMatches, eventToKey, eventToMods, mouseButtonToKey, synthVrBindings } from '../../config/keybindings';
import type { Modifier } from '../../config/keybindings';

/** The table's name for a DOM mouse button: the desktop editor's, where Mouse1 is the right button. */
export const domButtonToKey = (button: number | undefined): string => mouseButtonToKey(button ?? 0);

interface PressLike { button?: number; altKey?: boolean; ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean }

const modsOf = (e: PressLike): Modifier[] => {
  const mods: Modifier[] = [];
  if (e.ctrlKey || e.metaKey) mods.push('ctrl');
  if (e.altKey) mods.push('alt');
  if (e.shiftKey) mods.push('shift');
  return mods;
};

/** The bindings in force: Synth VR reads its own view of the table. */
function bindingsNow() {
  const s = useEditorStore.getState();
  return isSynthVrView(s.activeView) ? synthVrBindings(s.keybindings) : s.keybindings;
}

/** True when this press is the one bound to gesture `id`, on its primary or secondary slot, with exactly the
 *  modifiers bound. A press with a button the table has no name for matches nothing. */
export function gestureMatches(id: string, e: PressLike): boolean {
  const b = bindingsNow().find(x => x.id === id);
  if (!b) return false;
  const key = domButtonToKey(e.button);
  const mods = modsOf(e);
  return comboMatches(b.primary, key, mods) || comboMatches(b.secondary, key, mods);
}

/** True when this key press is the one bound to `id` (a key gesture such as the wall rotation keys). */
export function keyGestureMatches(id: string, e: KeyboardEvent): boolean {
  const b = bindingsNow().find(x => x.id === id);
  if (!b) return false;
  const mods = eventToMods(e);
  // A letter is matched by the physical key as well as the character it typed. The wall rotation read `e.code`
  // (KeyE, KeyQ) before it read the table, and with Alt held a Mac types something else: Option+E is `Dead` and
  // Option+Q is `œ`, so the character alone would stop the default Alt+E and Alt+Q from rotating a dragged wall.
  const physical = /^Key([A-Z])$/.exec(e.code)?.[1].toLowerCase();
  const keys = [eventToKey(e), ...(physical ? [physical] : [])];
  return keys.some(key => comboMatches(b.primary, key, mods) || comboMatches(b.secondary, key, mods));
}

/** The modifiers `id`'s primary slot holds, for a gesture that is held rather than pressed. */
export function gestureMods(id: string): Modifier[] | null {
  const b = bindingsNow().find(x => x.id === id);
  return b && b.primary.key !== 'None' ? b.primary.mods : null;
}

/**
 * Whether a press on a move handle belongs to the object under it: the drag and the mirror note (as above), the
 * rail-head drag, Remote Delete and go-to-object. The handles sit on the object's centre and show while Shift is
 * held, so a combo with Shift in it (Ctrl+Shift, say) lands on a handle and would otherwise be taken by it.
 * Kept apart from `isObjectGesturePress`, which also guards click handlers: the rail-head drag shares its
 * default combo with add-a-node, so it cannot make a click handler stand down.
 */
export function isHandlePassThroughPress(e: PressLike): boolean {
  if (isObjectGesturePress(e)) return true;
  if (!isSynthVrView(useEditorStore.getState().activeView)) return false;
  const press = { button: e.button ?? 0, altKey: e.altKey, ctrlKey: e.ctrlKey, metaKey: e.metaKey, shiftKey: e.shiftKey };
  return gestureMatches('drag_rail_head_only', press) || gestureMatches('remote_delete', press) || gestureMatches('snap_grid_to_object', press);
}

/**
 * Whether a left press or click is one of the gestures that act on the object under it (a drag, the
 * mirror note). Their click handlers and the handles behind them stand down for these, as they once did
 * for "Alt held". Outside Synth VR the old test stands.
 */
export function isObjectGesturePress(e: PressLike): boolean {
  if (!isSynthVrView(useEditorStore.getState().activeView)) return !!e.altKey;
  const press = { button: e.button ?? 0, altKey: e.altKey, ctrlKey: e.ctrlKey, metaKey: e.metaKey, shiftKey: e.shiftKey };
  return gestureMatches('drag_snap_object', press) || gestureMatches('add_mirror_note', press);
}
